import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { FoodPhotoPicker } from "./FoodPhotoPicker";
import { FoodSlideStrip } from "./FoodSlideStrip";
import { FoodTextEditor } from "./FoodTextEditor";
import {
  AlertCircle,
  Bookmark,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Copy,
  CircleEllipsis,
  Clock3,
  ExternalLink,
  Heart,
  Home,
  KeyRound,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  MapPin,
  MessageCircle,
  RefreshCw,
  Save,
  Send,
  ShieldCheck,
  Download,
  Film,
  Smartphone,
  Sparkles,
  Utensils,
  X, Layers,
  Trash2,
} from "lucide-react";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";

import oddyProfile from "../../assets/brand/oddy-profile-v2.png";
import type {
  OutroVariant,
  ReelBundle,
  ReelFileKind,
  ReviewPost,
  ScheduleSummary,
  SessionState,
  CollectionPost,
  FoodPost,
} from "../shared/ootd-review";

type View = "feed" | "schedule" | "outro" | "reels" | "collection" | "food";
type AuthMode = "loading" | "anonymous" | "authenticated";

interface BootstrapPayload {
  ok: true;
  posts: ReviewPost[];
  schedule: ScheduleSummary;
  session: SessionState;
}

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string,
  ) {
    super(message);
  }
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const response = await fetch(path, { ...init, headers, credentials: "same-origin" });
  const payload = await response.json().catch(() => null) as ({ error?: string; code?: string } & T) | null;
  if (!response.ok || !payload) {
    throw new ApiError(payload?.error ?? "요청을 처리하지 못했어요.", response.status, payload?.code ?? "request_failed");
  }
  return payload;
}

function formatKoreanDate(iso: string, withTime = true) {
  return new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    month: "long",
    day: "numeric",
    weekday: "short",
    ...(withTime ? { hour: "2-digit", minute: "2-digit", hour12: false } : {}),
  }).format(new Date(iso));
}

function toKstInput(iso: string) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}T${value.hour}:${value.minute}`;
}

function fromKstInput(value: string) {
  return new Date(`${value}:00+09:00`).toISOString();
}

function statusLabel(status: ReviewPost["status"]) {
  switch (status) {
    case "needs_outro": return "아웃트로 선택 필요";
    case "preview_ready": return "검수 가능";
    case "approved": return "승인 · 예약 대기";
    case "rejected": return "수정 요청";
    case "publishing": return "발행 중";
    case "published": return "발행 완료";
    case "publish_blocked": return "발행 연결 대기";
    case "manual_check_required": return "수동 확인 필요";
    default: return status;
  }
}

function statusTone(status: ReviewPost["status"]) {
  if (status === "published" || status === "approved") return "positive";
  if (status === "rejected" || status === "manual_check_required") return "negative";
  if (status === "publish_blocked" || status === "needs_outro") return "warning";
  return "neutral";
}

function LoginScreen({
  passkeyCount,
  onAuthenticated,
}: {
  passkeyCount: number;
  onAuthenticated: () => Promise<void>;
}) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState<"password" | "passkey" | null>(null);
  const [error, setError] = useState("");
  const passkeySupported = typeof window !== "undefined" && "PublicKeyCredential" in window;

  async function submitPassword(event: FormEvent) {
    event.preventDefault();
    setBusy("password");
    setError("");
    try {
      await api("/api/auth/password", {
        method: "POST",
        body: JSON.stringify({ password }),
      });
      setPassword("");
      await onAuthenticated();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "로그인하지 못했어요.");
    } finally {
      setBusy(null);
    }
  }

  async function passkeyLogin() {
    setBusy("passkey");
    setError("");
    try {
      const optionResult = await api<{
        ok: true;
        options: Parameters<typeof startAuthentication>[0]["optionsJSON"];
      }>("/api/auth/passkey/login/options", { method: "POST", body: "{}" });
      const response = await startAuthentication({ optionsJSON: optionResult.options });
      await api("/api/auth/passkey/login/verify", {
        method: "POST",
        body: JSON.stringify({ challenge: optionResult.options.challenge, response }),
      });
      await onAuthenticated();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "패스키 로그인을 완료하지 못했어요.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <main className="login-shell">
      <div className="login-noise" aria-hidden="true" />
      <section className="login-panel" aria-labelledby="login-title">
        <div className="login-brand">
          <img src={oddyProfile} alt="ODDY" />
          <div>
            <span>ODDY PRIVATE DESK</span>
            <strong>odd + daily</strong>
          </div>
        </div>
        <div className="login-copy">
          <p className="eyebrow">07 · 09 · 11 OOTD REVIEW</p>
          <h1 id="login-title">올리기 전,<br />피드 그대로 확인해요.</h1>
          <p>표지부터 본문, 아웃트로, 발행 순서까지<br />승인된 바이트만 다음 단계로 넘어갑니다.</p>
        </div>
        <form className="login-form" onSubmit={submitPassword}>
          <label htmlFor="review-password">접속 비밀번호</label>
          <div className="password-field">
            <LockKeyhole size={18} aria-hidden="true" />
            <input
              id="review-password"
              type="password"
              inputMode="numeric"
              autoComplete="current-password"
              placeholder="••••"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              maxLength={16}
              required
            />
          </div>
          <button className="primary-button login-button" disabled={busy !== null || password.length === 0}>
            {busy === "password" ? <LoaderCircle className="spin" size={18} /> : <ShieldCheck size={18} />}
            비밀번호로 확인
          </button>
        </form>
        {passkeySupported && passkeyCount > 0 && (
          <button className="passkey-button" type="button" onClick={passkeyLogin} disabled={busy !== null}>
            {busy === "passkey" ? <LoaderCircle className="spin" size={18} /> : <KeyRound size={18} />}
            지문 패스키로 바로 열기
          </button>
        )}
        {error && <p className="form-error" role="alert"><AlertCircle size={16} />{error}</p>}
        <p className="login-footnote"><Smartphone size={15} /> 첫 로그인 뒤 이 기기의 지문 패스키를 등록할 수 있어요.</p>
      </section>
      <aside className="login-preview" aria-hidden="true">
        <div className="preview-stack preview-stack-back" />
        <div className="preview-stack preview-stack-front">
          <span>ODDY</span>
          <strong>APPROVAL<br />BEFORE<br />PUBLISH</strong>
          <small>SEOUL · SCHEDULED KST</small>
        </div>
      </aside>
    </main>
  );
}

function SideNavigation({ view, onView, onLogout }: {
  view: View;
  onView: (view: View) => void;
  onLogout: () => void;
}) {
  const items: Array<{ id: View; label: string; icon: typeof Home }> = [
    { id: "feed", label: "피드 검수", icon: Home },
    { id: "schedule", label: "발행 일정", icon: CalendarDays },
    { id: "outro", label: "아웃트로", icon: Sparkles },
    { id: "reels", label: "릴스 원본", icon: Film },
    { id: "collection", label: "묶음 게시물", icon: Layers },
    { id: "food", label: "음식", icon: Utensils },
  ];
  return (
    <aside className="side-navigation">
      <button className="wordmark" onClick={() => onView("feed")} aria-label="ODDY 피드 검수 홈">
        <img src={oddyProfile} alt="" />
        <span>ODDY</span>
      </button>
      <nav aria-label="검수 메뉴">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <button key={item.id} className={view === item.id ? "active" : ""} onClick={() => onView(item.id)}>
              <Icon size={22} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>
      <div className="side-account">
        <div><img src={oddyProfile} alt="" /><span><strong>oddy.world.kr</strong><small>private editor</small></span></div>
        <button onClick={onLogout} aria-label="로그아웃"><LogOut size={19} /></button>
      </div>
    </aside>
  );
}

function PostSwitcher({ posts, activeId, onSelect }: {
  posts: ReviewPost[];
  activeId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="post-switcher" role="tablist" aria-label="검수할 모델">
      {posts.map((post) => (
        <button
          key={post.id}
          role="tab"
          aria-selected={post.id === activeId}
          className={post.id === activeId ? "active" : ""}
          onClick={() => onSelect(post.id)}
        >
          <span className={`model-dot model-${post.characterId}`}>{post.characterId}</span>
          <span><strong>{post.characterName}</strong><small>{post.locationName}</small></span>
        </button>
      ))}
    </div>
  );
}

function FeedImage({ src, alt }: { src: string; alt: string }) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setLoaded(false);
    setFailed(false);
  }, [src]);
  return (
    <div className={`feed-image-frame ${loaded ? "loaded" : ""}`}>
      {!loaded && !failed && <div className="image-skeleton" />}
      {failed ? (
        <div className="image-error"><AlertCircle size={26} /><span>이미지를 불러오지 못했어요.</span></div>
      ) : (
        <img src={src} alt={alt} onLoad={() => setLoaded(true)} onError={() => setFailed(true)} />
      )}
    </div>
  );
}

function InstagramPost({ post, slide, onSlide }: {
  post: ReviewPost;
  slide: number;
  onSlide: (index: number) => void;
}) {
  const assets = post.assets;
  const safeSlide = Math.min(slide, Math.max(assets.length - 1, 0));
  const asset = assets[safeSlide];
  // position 기반이면 legacy(8장)와 spec(8장)의 순서가 어긋난다. role 기준으로 붙인다.
  const roleLabels: Record<string, string> = {
    cover: "표지", environment: "배경", detail: "디테일", playful: "플레이풀",
    expression: "표정", motion: "모션", seated: "앉은 컷",
    "detail-lower": "하의 디테일", "detail-upper": "상의 디테일",
    outfit: "의상 정보", outro: "아웃트로",
  };
  return (
    <article className="instagram-post">
      <header className="instagram-post-header">
        <img src={oddyProfile} alt="ODDY 프로필" />
        <div>
          <strong>oddy.world.kr <span className="verified-dot"><Check size={9} /></span></strong>
          <small><MapPin size={12} /> {post.locationName}</small>
        </div>
        <button aria-label="게시물 옵션"><CircleEllipsis size={21} /></button>
      </header>
      <div className="carousel-shell">
        {asset ? (
          <FeedImage src={asset.url} alt={`${post.characterName} ${roleLabels[asset.role] ?? asset.role} 이미지`} />
        ) : (
          <div className="missing-outro">
            <Sparkles size={34} />
            <strong>아웃트로 선택이 필요해요</strong>
            <span>검수 메뉴에서 1안을 고르면 6번째 장이 연결됩니다.</span>
          </div>
        )}
        {safeSlide > 0 && (
          <button className="carousel-arrow left" aria-label="이전 이미지" onClick={() => onSlide(safeSlide - 1)}>
            <ChevronLeft size={23} />
          </button>
        )}
        {safeSlide < assets.length - 1 && (
          <button className="carousel-arrow right" aria-label="다음 이미지" onClick={() => onSlide(safeSlide + 1)}>
            <ChevronRight size={23} />
          </button>
        )}
        {asset && <span className="slide-counter">{safeSlide + 1}/{assets.length}</span>}
      </div>
      <div className="post-actions">
        <div><button aria-label="좋아요"><Heart size={25} /></button><button aria-label="댓글"><MessageCircle size={24} /></button><button aria-label="공유"><Send size={23} /></button></div>
        <div className="carousel-dots" aria-label={`${safeSlide + 1}번째 이미지`}>
          {assets.map((item, index) => <button key={item.id} className={index === safeSlide ? "active" : ""} onClick={() => onSlide(index)} aria-label={`${index + 1}번째 이미지`} />)}
        </div>
        <button aria-label="저장"><Bookmark size={24} /></button>
      </div>
      <div className="post-copy">
        <strong>좋아요 2,406개</strong>
        <p><b>oddy.world.kr</b> {post.caption}</p>
        <button>댓글 48개 모두 보기</button>
        <small>방금 전 · 번역 보기</small>
      </div>
    </article>
  );
}

function ReviewInspector({
  post,
  busy,
  onSave,
  onApprove,
  onPublishNow,
  onReject,
  publisherReady,
  onRegisterPasskey,
  passkeyCount,
}: {
  post: ReviewPost;
  busy: string | null;
  onSave: (caption: string, scheduledAt: string) => Promise<void>;
  onApprove: () => Promise<void>;
  onPublishNow: () => Promise<void>;
  onReject: () => Promise<void>;
  publisherReady: boolean;
  onRegisterPasskey: () => Promise<void>;
  passkeyCount: number;
}) {
  const [caption, setCaption] = useState(post.caption);
  const [scheduledAt, setScheduledAt] = useState(toKstInput(post.scheduledAt));
  useEffect(() => {
    setCaption(post.caption);
    setScheduledAt(toKstInput(post.scheduledAt));
  }, [post.id, post.revision, post.caption, post.scheduledAt]);
  const dirty = caption !== post.caption || scheduledAt !== toKstInput(post.scheduledAt);
  const qualityPass = post.qualityGate.status === "pass";
  const canApprove = qualityPass && post.status !== "approved" && post.status !== "published";
  return (
    <aside className="review-inspector">
      <header>
        <div><span>REVIEW DESK</span><h2>게시 전 최종 확인</h2></div>
        <span className={`status-pill ${statusTone(post.status)}`}>{statusLabel(post.status)}</span>
      </header>
      <section className="inspector-section compact-grid">
        <div><small>MODEL</small><strong>{post.characterName}</strong><span>{post.characterId}</span></div>
        <div><small>SLIDES</small><strong>{post.assets.length}장</strong><span>{qualityPass ? "낙산 기준 통과" : "품질 잠금 차단"}</span></div>
        <div><small>OUTRO</small><strong>{post.selectedOutro ?? "—"}</strong><span>{post.selectedOutro === "C" ? "AFTERGLOW" : "선택 필요"}</span></div>
        <div><small>REVISION</small><strong>v{post.revision}</strong><span>hash locked</span></div>
      </section>
      {post.reviewNote && <div className="review-note"><AlertCircle size={17} /><span>{post.reviewNote}</span></div>}
      {post.existingLiveUrl && (
        <a className="existing-live" href={post.existingLiveUrl} target="_blank" rel="noreferrer">
          <span><strong>기존 4장 게시물</strong><small>현재 6장 재게시 검토본과 별개예요.</small></span><ExternalLink size={17} />
        </a>
      )}
      {post.outfit && (
        <a className="existing-live" href={post.outfit.appUrl ?? post.outfit.url} target="_blank" rel="noreferrer">
          <span>
            <strong>착장 상품 보기 · {post.outfit.brand ?? "ABLY"}</strong>
            <small>{post.outfit.productName ?? "에이블리 상품 페이지"}{post.outfit.price ? ` · ${post.outfit.price}` : ""}</small>
          </span>
          <ExternalLink size={17} />
        </a>
      )}
      <section className="inspector-section editor-fields">
        <label>
          <span>본문 <small>{caption.length}/2,200</small></span>
          <textarea value={caption} onChange={(event) => setCaption(event.target.value)} maxLength={2200} rows={11} />
        </label>
        <label>
          <span>예약 시각 <small>KST</small></span>
          <input type="datetime-local" value={scheduledAt} onChange={(event) => setScheduledAt(event.target.value)} />
        </label>
        <button className="save-button" disabled={!dirty || busy !== null} onClick={() => onSave(caption, fromKstInput(scheduledAt))}>
          {busy === "save" ? <LoaderCircle className="spin" size={17} /> : <Save size={17} />} 변경 저장
        </button>
        {dirty && <p className="dirty-note">저장하면 이전 승인은 자동으로 무효화됩니다.</p>}
      </section>
      <section className="approval-actions">
        <button className="reject-button" disabled={busy !== null || post.status === "published"} onClick={onReject}><X size={18} /> 수정 요청</button>
        <button className="approve-button" disabled={!canApprove || busy !== null} onClick={onApprove}>
          {busy === "approve" ? <LoaderCircle className="spin" size={18} /> : <CheckCircle2 size={18} />}
          {post.status === "approved" ? "승인 완료" : "이대로 승인"}
        </button>
      </section>
      <button className="publish-now-button" disabled={!(["approved", "publish_blocked"] as const).includes(post.status as "approved" | "publish_blocked") || dirty || busy !== null || !publisherReady} onClick={onPublishNow}>
        {busy === "publish-now" ? <LoaderCircle className="spin" size={18} /> : <Send size={18} />}
        {post.status === "publish_blocked" ? "안전하게 다시 게시" : "바로 게시하기"}
      </button>
      {!publisherReady && <p className="approval-blocker"><LockKeyhole size={15} /> Meta 발행 자격증명이 연결되면 바로 게시할 수 있어요.</p>}
      {!post.selectedOutro && <p className="approval-blocker"><Sparkles size={15} /> 아웃트로를 고르면 승인 버튼이 열려요.</p>}
      {!qualityPass && post.qualityGate.reasons.map((reason) => (
        <p className="approval-blocker" key={reason}><ShieldCheck size={15} /> {reason}</p>
      ))}
      <section className="passkey-card">
        <div><KeyRound size={19} /><span><strong>지문 패스키</strong><small>{passkeyCount > 0 ? `${passkeyCount}개 등록됨` : "이 기기에 아직 없음"}</small></span></div>
        <button onClick={onRegisterPasskey} disabled={busy !== null}>{passkeyCount > 0 ? "추가" : "등록"}</button>
      </section>
    </aside>
  );
}

function OutroStudio({ posts, onSelect, busy }: {
  posts: ReviewPost[];
  onSelect: (variant: OutroVariant) => Promise<void>;
  busy: string | null;
}) {
  const current = posts[0]?.selectedOutro ?? null;
  const variants: Array<{ id: OutroVariant; title: string; description: string }> = [
    { id: "C", title: "AFTERGLOW", description: "마지막 장면의 잔상을 남기는 감성 엔딩" },
  ];
  return (
    <main className="studio-page">
      <header className="page-heading">
        <span>OUTRO SYSTEM</span>
        <h1>마지막 한 장의 분위기</h1>
        <p>하린 낙산에서 검증된 C · AFTERGLOW가 07·09·11 전 회차에 고정됩니다.</p>
      </header>
      <div className="outro-options">
        {variants.map((variant) => {
          const samples = posts.map((post) => post.outroCandidates.find((asset) => asset.variant === variant.id)).filter(Boolean);
          const selected = current === variant.id;
          return (
            <article key={variant.id} className={`outro-option ${selected ? "selected" : ""}`}>
              <header>
                <span className="outro-index">{variant.id}</span>
                <div><h2>{variant.title}</h2><p>{variant.description}</p></div>
                {selected && <span className="selected-badge"><Check size={14} /> 선택됨</span>}
              </header>
              <div className="outro-samples">
                {samples.map((sample) => sample && (
                  <figure key={sample.id}><img src={sample.url} alt={`${variant.title} ${sample.postId}`} /><figcaption>{sample.postId.includes("-07-") ? "하린" : sample.postId.includes("-09-") ? "지안" : "채원"}</figcaption></figure>
                ))}
              </div>
              <button disabled={selected || busy !== null} onClick={() => onSelect(variant.id)}>
                {busy === `outro-${variant.id}` ? <LoaderCircle className="spin" size={17} /> : selected ? <Check size={17} /> : <Sparkles size={17} />}
                {selected ? "현재 적용 중" : "이 스타일 적용"}
              </button>
            </article>
          );
        })}
      </div>
      <div className="outro-lock-note"><ShieldCheck size={19} /><span><strong>현재 확정: C · AFTERGLOW</strong><small>우측 상단 작은 로고 제거 · 감성 세리프 굵기 조정 · 큰 ODDY 캐릭터 유지</small></span></div>
    </main>
  );
}

function SchedulePage({ posts, schedule }: { posts: ReviewPost[]; schedule: ScheduleSummary }) {
  return (
    <main className="schedule-page">
      <header className="page-heading">
        <span>PUBLISH QUEUE</span>
        <h1>고른 게시물을 원하는 때에</h1>
        <p>인플루언서와 관계없이 게시물별로 즉시 발행하거나 원하는 예약 시각에 발행할 수 있습니다.</p>
      </header>
      <section className={`publisher-state ${schedule.credentialState === "ready" ? "ready" : "waiting"}`}>
        <div>{schedule.credentialState === "ready" ? <CheckCircle2 size={24} /> : <LockKeyhole size={24} />}</div>
        <span><strong>{schedule.credentialState === "ready" ? "자동 발행 연결됨" : "검수·예약은 작동 중, 실제 발행 연결은 잠금 상태"}</strong><small>{schedule.credentialState === "ready" ? `승인된 게시물을 ${schedule.pollIntervalMinutes}분마다 확인해 지정 시각에 발행합니다.` : "Meta 자격증명이 연결되기 전에는 외부 발행 요청을 보내지 않습니다."}</small></span>
      </section>
      <section className="rotation-card">
        <header><span>APPROVED POSTS</span><strong>Asia/Seoul · {schedule.publishTime}</strong></header>
        <div className="rotation-track">
          {posts.map((post, index) => {
            const qualityPass = post.qualityGate.status === "pass";
            return (
            <article key={post.id}>
              <div className={`timeline-marker ${statusTone(post.status)}`}>{index + 1}</div>
              <div className="schedule-content">
                <div><span className={`model-dot model-${post.characterId}`}>{post.characterId}</span><span><strong>{post.characterName} · {post.locationName}</strong><small>{formatKoreanDate(post.scheduledAt)}</small></span></div>
                <span className={`status-pill ${statusTone(post.status)}`}>{statusLabel(post.status)}</span>
                <div className="schedule-checks">
                  <span className={qualityPass ? "done" : ""}><Check size={14} /> 낙산 품질 잠금</span>
                  <span className={post.selectedOutro === "C" ? "done" : ""}><Check size={14} /> C 아웃트로</span>
                  <span className={post.status === "approved" || post.status === "published" ? "done" : ""}><Check size={14} /> 최종 승인</span>
                </div>
              </div>
            </article>
            );
          })}
        </div>
      </section>
      <section className="safety-contract">
        <ShieldCheck size={20} />
        <div><strong>승인한 파일 그대로만 발행</strong><p>본문·시각·이미지 중 하나라도 바뀌면 해시가 달라지고 예약 승인도 즉시 해제됩니다. 발행 응답이 애매하면 자동 재시도하지 않고 수동 확인으로 멈춥니다.</p></div>
      </section>
    </main>
  );
}


function formatBytes(bytes: number) {
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

const REEL_KIND_LABEL: Record<ReelFileKind, string> = {
  reel: "릴스 본편",
  cover: "커버 이미지",
  outro: "아웃트로 시안",
  reference: "참고 자료",
};

function ReelDownloads({ bundles, loading, onCopyCaption }: {
  bundles: ReelBundle[];
  loading: boolean;
  onCopyCaption: (caption: string) => void;
}) {
  if (loading) {
    return <main className="reels-page"><div className="reels-empty"><LoaderCircle className="spin" size={22} /><span>릴스 원본을 불러오는 중</span></div></main>;
  }
  if (bundles.length === 0) {
    return (
      <main className="reels-page">
        <header className="page-heading">
          <h1>릴스 원본</h1>
          <p>폰으로 받아서 인스타 앱에서 직접 올리는 파일입니다.</p>
        </header>
        <div className="reels-empty"><Film size={22} /><span>아직 올라온 릴스 원본이 없어요.</span></div>
      </main>
    );
  }
  return (
    <main className="reels-page">
      <header className="page-heading">
        <h1>릴스 원본</h1>
        <p>폰으로 받아서 인스타 앱에서 직접 올립니다. 앱에서 올려야 트렌딩 오디오를 얹을 수 있어요.</p>
      </header>
      {bundles.map((bundle) => {
        const reel = bundle.files.find((file) => file.kind === "reel");
        const rest = bundle.files.filter((file) => file.kind !== "reel");
        return (
          <section className="reel-bundle" key={bundle.id}>
            {reel && (
              <video className="reel-preview" src={`${reel.url}&inline=1`} controls playsInline preload="metadata" />
            )}
            <div className="reel-bundle-head">
              <div>
                <h2>{bundle.title}</h2>
                <p>
                  {bundle.characterName} · {bundle.durationSec ? `${bundle.durationSec.toFixed(2)}초` : "길이 미상"}
                  {" · "}{new Date(bundle.createdAt).toLocaleDateString("ko-KR")}
                </p>
              </div>
              {reel && (
                <a className="reel-primary-download" href={reel.url} download={reel.filename}>
                  <Download size={19} /><span>본편 받기</span><small>{formatBytes(reel.bytes)}</small>
                </a>
              )}
            </div>

            {bundle.note && <p className="reel-note">{bundle.note}</p>}

            <div className="reel-file-list">
              {rest.map((file) => (
                <a className="reel-file" key={file.id} href={file.url} download={file.filename}>
                  <span className="reel-file-kind">{REEL_KIND_LABEL[file.kind]}</span>
                  <span className="reel-file-label">{file.label}</span>
                  <span className="reel-file-size">{formatBytes(file.bytes)}</span>
                  <Download size={17} />
                </a>
              ))}
            </div>

            {bundle.caption && (
              <div className="reel-caption">
                <div className="reel-caption-head">
                  <strong>캡션</strong>
                  <button onClick={() => onCopyCaption(bundle.caption)}><Copy size={15} /><span>복사</span></button>
                </div>
                <pre>{bundle.caption}</pre>
              </div>
            )}
          </section>
        );
      })}
      <section className="safety-contract">
        <ShieldCheck size={20} />
        <div><strong>업로드는 앱에서 직접</strong><p>이 파일들은 Graph API로 나가지 않습니다. 폰에서 받아 인스타 앱으로 올려야 트렌딩 오디오를 선택할 수 있고, 커버도 업로드 화면에서 직접 지정합니다.</p></div>
      </section>
    </main>
  );
}

function CollectionReview({ collections, loading, busy, onPublish, onCopyCaption }: {
  collections: CollectionPost[];
  loading: boolean;
  busy: string | null;
  onPublish: (id: string) => void;
  onCopyCaption: (caption: string) => void;
}) {
  if (loading) {
    return <main className="collection-page"><div className="collection-empty"><LoaderCircle className="spin" size={22} /><span>묶음 게시물을 불러오는 중</span></div></main>;
  }
  if (collections.length === 0) {
    return (
      <main className="collection-page">
        <header className="collection-head"><h1>묶음 게시물</h1><p>여러 룩을 한 게시물로 묶은 캐러셀이에요.</p></header>
        <div className="collection-empty"><Layers size={22} /><span>아직 올라온 묶음 게시물이 없어요.</span></div>
      </main>
    );
  }
  return (
    <main className="collection-page">
      <header className="collection-head"><h1>묶음 게시물</h1><p>여러 룩을 한 게시물로 묶은 캐러셀이에요. 확인 후 게시하면 바로 올라가요.</p></header>
      {collections.map((item) => {
        const publishing = busy === `collection:${item.id}`;
        const done = item.status === "published";
        return (
          <section className="collection-card" key={item.id}>
            <div className="collection-meta">
              <div>
                <h2>{item.title}</h2>
                {item.subtitle && <p className="collection-sub">{item.subtitle}</p>}
                <p className="collection-count">{item.assets.length}장 · {STATUS_LABEL[item.status] ?? item.status}</p>
              </div>
              <div className="collection-actions">
                <button className="ghost" onClick={() => onCopyCaption(item.caption)}>캡션 복사</button>
                <button
                  className="primary"
                  disabled={publishing || done || item.status === "publishing" || item.status === "manual_check_required"}
                  onClick={() => onPublish(item.id)}
                >
                  {publishing ? <LoaderCircle className="spin" size={17} /> : null}
                  {done ? "게시 완료" : publishing ? "게시하는 중" : "게시하기"}
                </button>
              </div>
            </div>
            {item.note && <p className="collection-note">{item.note}</p>}
            {item.reviewNote && <p className="collection-warn">{item.reviewNote}</p>}
            {item.publishedUrl && (
              <p className="collection-link"><a href={item.publishedUrl} target="_blank" rel="noreferrer">게시물 열기</a></p>
            )}
            <div className="collection-strip">
              {item.assets.map((asset) => (
                <figure key={asset.id}>
                  <img src={asset.url} alt={asset.label} loading="lazy" />
                  <figcaption><b>{asset.position}. {asset.label}</b>{asset.sublabel && <span>{asset.sublabel}</span>}</figcaption>
                </figure>
              ))}
            </div>
            <details className="collection-caption">
              <summary>캡션 보기</summary>
              <pre>{item.caption}</pre>
            </details>
          </section>
        );
      })}
    </main>
  );
}

// 슬라이드는 표지 1장 + 가게마다 1장이다 (Instagram 캐러셀 상한 10장).
function venuesOf(post: FoodPost) {
  return post.assets
    .filter((asset) => asset.role === "venue")
    .map((asset, index) => ({ index, name: asset.label }));
}

function FoodReview({ posts, loading, busy, onPublish, onCopyCaption, onRebuilt, onSlideError, onDeletePost }: {
  posts: FoodPost[];
  loading: boolean;
  busy: string | null;
  onPublish: (id: string) => void;
  onCopyCaption: (caption: string) => void;
  onRebuilt: (post: FoodPost) => void;
  onSlideError: (message: string) => void;
  onDeletePost: (post: FoodPost) => void;
}) {
  if (loading) {
    return <main className="food-page"><div className="food-empty"><LoaderCircle className="spin" size={22} /><span>음식 게시물을 불러오는 중</span></div></main>;
  }
  if (posts.length === 0) {
    return (
      <main className="food-page">
        <header className="food-head"><div><span>ODDY FOOD DESK</span><h1>음식</h1><p>서울 맛집 캐러셀을 검수하고 게시하는 전용 대기열입니다.</p></div></header>
        <div className="food-empty"><Utensils size={23} /><span>아직 준비된 음식 게시물이 없어요.</span></div>
      </main>
    );
  }
  const ready = posts.filter((post) => post.status !== "published").length;
  const published = posts.filter((post) => post.status === "published").length;
  return (
    <main className="food-page">
      <header className="food-head">
        <div><span>ODDY FOOD DESK</span><h1>음식</h1><p>표지 1장과 맛집 카드 9장을 그대로 확인한 뒤 게시할 수 있어요.</p></div>
        <dl>
          <div><dt>전체</dt><dd>{posts.length}</dd></div>
          <div><dt>게시 가능</dt><dd>{ready}</dd></div>
          <div><dt>게시 완료</dt><dd>{published}</dd></div>
        </dl>
      </header>
      <div className="food-list">
        {posts.map((post) => {
          const publishing = busy === `food:${post.id}`;
          const done = post.status === "published";
          const blocked = post.status === "publishing" || post.status === "manual_check_required";
          const canPublish = !done && !blocked && post.assets.length > 0;
          return (
            <section className="food-card" key={post.id}>
              <div className="food-card-copy">
                <div className="food-title-row">
                  <div>
                    <span className="food-topic">{post.topicSlug.replaceAll("-", " ")}</span>
                    <h2>{post.title}</h2>
                    {post.subtitle && <p>{post.subtitle}</p>}
                  </div>
                  <div className="food-badges">
                    <span className={`food-status ${done ? "done" : ""}`}>{STATUS_LABEL[post.status] ?? post.status}</span>
                  </div>
                </div>
                {post.note && <p className="food-note">{post.note}</p>}
                {post.reviewNote && <p className="food-warning">{post.reviewNote}</p>}
                <details className="food-picker">
                  <summary>문구 수정</summary>
                  <FoodTextEditor post={post} onChanged={onRebuilt} onError={onSlideError} />
                </details>
                <details className="food-picker">
                  <summary>사진 선택 · 크롭</summary>
                  <FoodPhotoPicker
                    postId={post.id}
                    venues={venuesOf(post)}
                    onRebuilt={(next) => onRebuilt(next as FoodPost)}
                  />
                </details>
                <div className="food-actions">
                  <button className="ghost" onClick={() => onCopyCaption(post.caption)}><Copy size={16} /> 캡션 복사</button>
                  <button
                    className="ghost food-delete"
                    disabled={done || busy === `food-del:${post.id}`}
                    title={done ? "발행된 게시물은 지우지 않아요." : "게시물 전체 삭제"}
                    onClick={() => onDeletePost(post)}
                  >
                    {busy === `food-del:${post.id}` ? <LoaderCircle className="spin" size={16} /> : <Trash2 size={16} />}
                    삭제
                  </button>
                  <button
                    className="primary food-publish"
                    disabled={!canPublish || publishing}
                    onClick={() => onPublish(post.id)}
                  >
                    {publishing ? <LoaderCircle className="spin" size={17} /> : <Send size={17} />}
                    {done ? "게시 완료" : publishing ? "게시하는 중" : "Instagram에 게시"}
                  </button>
                </div>
                {post.publishedUrl && <a className="food-published-link" href={post.publishedUrl} target="_blank" rel="noreferrer">게시물 열기 <ExternalLink size={14} /></a>}
                <details className="food-caption">
                  <summary>캡션 전체 보기</summary>
                  <pre>{post.caption}</pre>
                </details>
              </div>
              <FoodSlideStrip
                post={post}
                editable={!done && !blocked}
                onChanged={onRebuilt}
                onError={onSlideError}
              />
            </section>
          );
        })}
      </div>
    </main>
  );
}

const STATUS_LABEL: Record<string, string> = {
  preview_ready: "검수 대기",
  publishing: "게시 중",
  published: "게시 완료",
  publish_blocked: "게시 실패",
  manual_check_required: "수동 확인 필요",
};

function MobileNavigation({ view, onView }: { view: View; onView: (view: View) => void }) {
  const items: Array<{ id: View; label: string; icon: typeof Home }> = [
    { id: "feed", label: "피드", icon: Home },
    { id: "schedule", label: "일정", icon: CalendarDays },
    { id: "outro", label: "아웃트로", icon: Sparkles },
    { id: "reels", label: "릴스", icon: Film },
    { id: "collection", label: "묶음", icon: Layers },
    { id: "food", label: "음식", icon: Utensils },
  ];
  return (
    <nav className="mobile-navigation" aria-label="모바일 검수 메뉴">
      {items.map((item) => {
        const Icon = item.icon;
        return <button key={item.id} className={view === item.id ? "active" : ""} onClick={() => onView(item.id)}><Icon size={21} /><span>{item.label}</span></button>;
      })}
    </nav>
  );
}

export function OotdReviewApp() {
  const [authMode, setAuthMode] = useState<AuthMode>("loading");
  const [session, setSession] = useState<SessionState>({ authenticated: false, passkeyCount: 0 });
  const [posts, setPosts] = useState<ReviewPost[]>([]);
  const [schedule, setSchedule] = useState<ScheduleSummary | null>(null);
  const [view, setView] = useState<View>("feed");
  const [activeId, setActiveId] = useState("");
  const [slide, setSlide] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<{ tone: "success" | "error"; message: string } | null>(null);
  const [reelBundles, setReelBundles] = useState<ReelBundle[]>([]);
  const [reelsLoading, setReelsLoading] = useState(false);
  const [reelsLoaded, setReelsLoaded] = useState(false);
  const [collections, setCollections] = useState<CollectionPost[]>([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [collectionsLoaded, setCollectionsLoaded] = useState(false);
  const [foodPosts, setFoodPosts] = useState<FoodPost[]>([]);
  const [foodLoading, setFoodLoading] = useState(false);
  const [foodLoaded, setFoodLoaded] = useState(false);

  const showToast = useCallback((tone: "success" | "error", message: string) => {
    setToast({ tone, message });
    window.setTimeout(() => setToast(null), 4200);
  }, []);

  const loadBootstrap = useCallback(async () => {
    try {
      const result = await api<BootstrapPayload>("/api/bootstrap");
      setPosts(result.posts);
      setSchedule(result.schedule);
      setSession(result.session);
      setActiveId((current) => current && result.posts.some((post) => post.id === current) ? current : result.posts[0]?.id ?? "");
      setAuthMode("authenticated");
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        const result = await api<{ ok: true; session: SessionState }>("/api/auth/session");
        setSession(result.session);
        setAuthMode("anonymous");
        return;
      }
      showToast("error", error instanceof Error ? error.message : "검수 데이터를 불러오지 못했어요.");
      setAuthMode("anonymous");
    }
  }, [showToast]);

  useEffect(() => { void loadBootstrap(); }, [loadBootstrap]);

  // Reel bundles carry a video element per row, so they load on first visit to
  // the tab rather than on every bootstrap.
  useEffect(() => {
    if (view !== "reels" || reelsLoaded || reelsLoading) return;
    setReelsLoading(true);
    void api<{ ok: true; bundles: ReelBundle[] }>("/api/reels")
      .then((result) => { setReelBundles(result.bundles); setReelsLoaded(true); })
      .catch((error: unknown) => showToast("error", error instanceof Error ? error.message : "릴스 원본을 불러오지 못했어요."))
      .finally(() => setReelsLoading(false));
  }, [view, reelsLoaded, reelsLoading, showToast]);

  // 묶음 게시물은 장수가 많아 탭을 처음 열 때만 불러온다.
  useEffect(() => {
    if (view !== "collection" || collectionsLoaded || collectionsLoading) return;
    setCollectionsLoading(true);
    void api<{ ok: true; collections: CollectionPost[] }>("/api/collections")
      .then((result) => { setCollections(result.collections); setCollectionsLoaded(true); })
      .catch((error: unknown) => showToast("error", error instanceof Error ? error.message : "묶음 게시물을 불러오지 못했어요."))
      .finally(() => setCollectionsLoading(false));
  }, [view, collectionsLoaded, collectionsLoading, showToast]);

  useEffect(() => {
    if (view !== "food" || foodLoaded || foodLoading) return;
    setFoodLoading(true);
    void api<{ ok: true; posts: FoodPost[] }>("/api/food")
      .then((result) => { setFoodPosts(result.posts); setFoodLoaded(true); })
      .catch((error: unknown) => showToast("error", error instanceof Error ? error.message : "음식 게시물을 불러오지 못했어요."))
      .finally(() => setFoodLoading(false));
  }, [view, foodLoaded, foodLoading, showToast]);

  const publishCollection = useCallback(async (id: string) => {
    setBusy(`collection:${id}`);
    try {
      const result = await api<{ ok: true; collection: CollectionPost }>(`/api/collections/${encodeURIComponent(id)}/publish-now`, { method: "POST" });
      setCollections((prev) => prev.map((item) => (item.id === id ? result.collection : item)));
      showToast("success", "게시했어요.");
    } catch (error: unknown) {
      showToast("error", error instanceof Error ? error.message : "게시에 실패했어요.");
      void api<{ ok: true; collections: CollectionPost[] }>("/api/collections")
        .then((refreshed) => setCollections(refreshed.collections))
        .catch(() => undefined);
    } finally {
      setBusy(null);
    }
  }, [showToast]);

  const publishFood = useCallback(async (id: string) => {
    const post = foodPosts.find((candidate) => candidate.id === id);
    if (!post) return;
    if (!window.confirm(`${post.title} 10장과 현재 캡션을 Instagram @oddy.world.kr에 지금 게시할까요?\n게시 요청 뒤에는 자동으로 다시 시도하지 않습니다.`)) return;
    setBusy(`food:${id}`);
    try {
      const result = await api<{ ok: true; post: FoodPost }>(`/api/food/${encodeURIComponent(id)}/publish-now`, { method: "POST", body: "{}" });
      setFoodPosts((current) => current.map((candidate) => candidate.id === id ? result.post : candidate));
      showToast("success", "음식 게시물을 Instagram에 게시했어요.");
    } catch (error: unknown) {
      showToast("error", error instanceof Error ? error.message : "음식 게시에 실패했어요.");
      void api<{ ok: true; posts: FoodPost[] }>("/api/food")
        .then((refreshed) => setFoodPosts(refreshed.posts))
        .catch(() => undefined);
    } finally {
      setBusy(null);
    }
  }, [foodPosts, showToast]);

  // 게시 전 게시물 정리. 발행된 것은 서버가 거부한다 — 기록이 남아야 한다.
  const deleteFoodPost = useCallback(async (post: FoodPost) => {
    if (!window.confirm(`"${post.title}" 게시물을 통째로 지울까요?\n슬라이드와 후보 사진이 모두 사라지고 되돌릴 수 없어요.`)) return;
    setBusy(`food-del:${post.id}`);
    try {
      const result = await api<{ ok: true; slides: number; files: number }>(
        `/api/food/${encodeURIComponent(post.id)}`, { method: "DELETE" });
      setFoodPosts((current) => current.filter((candidate) => candidate.id !== post.id));
      showToast("success", `게시물을 지웠어요 (슬라이드 ${result.slides}장 · 파일 ${result.files}개).`);
    } catch (error: unknown) {
      showToast("error", error instanceof Error ? error.message : "게시물을 지우지 못했어요.");
    } finally {
      setBusy(null);
    }
  }, [showToast]);

  const copyCaption = useCallback(async (caption: string) => {
    try {
      await navigator.clipboard.writeText(caption);
      showToast("success", "캡션을 복사했어요.");
    } catch {
      showToast("error", "복사가 막혀 있어요. 길게 눌러 직접 선택해 주세요.");
    }
  }, [showToast]);
  useEffect(() => { setSlide(0); }, [activeId]);
  const activePost = useMemo(() => posts.find((post) => post.id === activeId) ?? posts[0], [activeId, posts]);

  function replacePost(post: ReviewPost) {
    setPosts((current) => current.map((candidate) => candidate.id === post.id ? post : candidate));
  }

  async function savePost(caption: string, scheduledAt: string) {
    if (!activePost) return;
    setBusy("save");
    try {
      const result = await api<{ ok: true; post: ReviewPost }>(`/api/posts/${encodeURIComponent(activePost.id)}`, {
        method: "PATCH",
        body: JSON.stringify({ caption, scheduledAt }),
      });
      replacePost(result.post);
      showToast("success", "본문과 발행 시각을 저장했어요. 이전 승인은 해제됐습니다.");
    } catch (error) {
      showToast("error", error instanceof Error ? error.message : "저장하지 못했어요.");
    } finally { setBusy(null); }
  }

  async function approveActive() {
    if (!activePost || !window.confirm(`${activePost.characterName} 게시물 6장과 현재 본문을 승인할까요?\n승인된 해시만 예약 발행 대상이 됩니다.`)) return;
    setBusy("approve");
    try {
      const result = await api<{ ok: true; post: ReviewPost }>(`/api/posts/${encodeURIComponent(activePost.id)}/approve`, {
        method: "POST",
        body: "{}",
      });
      replacePost(result.post);
      showToast("success", `${activePost.characterName} 게시물을 승인했어요.`);
    } catch (error) {
      showToast("error", error instanceof Error ? error.message : "승인하지 못했어요.");
    } finally { setBusy(null); }
  }

  async function publishNowActive() {
    if (!activePost || !["approved", "publish_blocked"].includes(activePost.status)) return;
    const retryLine = activePost.status === "publish_blocked"
      ? "\n이전 시도는 발행 요청 전에 중단된 경우에만 서버가 재시도를 허용합니다."
      : "";
    if (!window.confirm(`${activePost.characterName} 게시물을 지금 Instagram에 게시할까요?\n승인된 6장과 본문 그대로 발행됩니다.${retryLine}`)) return;
    setBusy("publish-now");
    try {
      const result = await api<{ ok: true; post: ReviewPost; result: { outcome: string; permalink?: string | null } }>(`/api/posts/${encodeURIComponent(activePost.id)}/publish-now`, {
        method: "POST",
        body: "{}",
      });
      replacePost(result.post);
      if (result.result.outcome !== "published") throw new Error(`바로 게시가 완료되지 않았어요: ${result.result.outcome}`);
      showToast("success", "Instagram 게시가 완료됐어요.");
    } catch (error) {
      showToast("error", error instanceof Error ? error.message : "바로 게시하지 못했어요.");
    } finally { setBusy(null); }
  }

  async function rejectActive() {
    if (!activePost) return;
    const note = window.prompt("수정할 내용을 짧게 적어주세요.", "인물 질감과 포즈 재확인");
    if (note === null) return;
    setBusy("reject");
    try {
      const result = await api<{ ok: true; post: ReviewPost }>(`/api/posts/${encodeURIComponent(activePost.id)}/reject`, {
        method: "POST",
        body: JSON.stringify({ note }),
      });
      replacePost(result.post);
      showToast("success", "수정 요청으로 표시했어요.");
    } catch (error) {
      showToast("error", error instanceof Error ? error.message : "수정 요청을 저장하지 못했어요.");
    } finally { setBusy(null); }
  }

  async function chooseOutro(variant: OutroVariant) {
    if (!window.confirm(`${variant} · AFTERGLOW를 07·09·11 공통 아웃트로로 잠글까요?\n기존 승인이 있다면 해제됩니다.`)) return;
    setBusy(`outro-${variant}`);
    try {
      const result = await api<{ ok: true; posts: ReviewPost[] }>("/api/outro/select", {
        method: "POST",
        body: JSON.stringify({ variant }),
      });
      setPosts(result.posts);
      showToast("success", `${variant}안을 공통 아웃트로로 적용했어요.`);
    } catch (error) {
      showToast("error", error instanceof Error ? error.message : "아웃트로를 바꾸지 못했어요.");
    } finally { setBusy(null); }
  }

  async function registerPasskey() {
    if (!("PublicKeyCredential" in window)) {
      showToast("error", "이 브라우저는 지문 패스키 등록을 지원하지 않아요.");
      return;
    }
    setBusy("passkey-register");
    try {
      const optionResult = await api<{
        ok: true;
        options: Parameters<typeof startRegistration>[0]["optionsJSON"];
      }>("/api/auth/passkey/register/options", { method: "POST", body: "{}" });
      const response = await startRegistration({ optionsJSON: optionResult.options });
      await api("/api/auth/passkey/register/verify", {
        method: "POST",
        body: JSON.stringify({ challenge: optionResult.options.challenge, response }),
      });
      await loadBootstrap();
      showToast("success", "이 기기의 지문 패스키를 등록했어요.");
    } catch (error) {
      showToast("error", error instanceof Error ? error.message : "패스키를 등록하지 못했어요.");
    } finally { setBusy(null); }
  }

  async function handleLogout() {
    setBusy("logout");
    try { await api("/api/auth/logout", { method: "POST", body: "{}" }); } catch { /* cookie still expires client-side on normal response */ }
    setPosts([]);
    setSchedule(null);
    setReelBundles([]);
    setReelsLoaded(false);
    setFoodPosts([]);
    setFoodLoaded(false);
    setSession((current) => ({ authenticated: false, passkeyCount: current.passkeyCount }));
    setAuthMode("anonymous");
    setBusy(null);
  }

  if (authMode === "loading") {
    return <main className="loading-screen"><img src={oddyProfile} alt="ODDY" /><LoaderCircle className="spin" size={24} /><span>검수 피드를 여는 중</span></main>;
  }
  if (authMode === "anonymous") return <LoginScreen passkeyCount={session.passkeyCount} onAuthenticated={loadBootstrap} />;

  return (
    <div className="app-shell">
      <SideNavigation view={view} onView={setView} onLogout={() => void handleLogout()} />
      <div className="mobile-topbar"><button onClick={() => setView("feed")}><img src={oddyProfile} alt="" /><strong>ODDY</strong></button><span>PRIVATE REVIEW</span></div>
      {view === "feed" && activePost && (
        <main className="feed-layout">
          <section className="feed-column">
            <PostSwitcher posts={posts} activeId={activePost.id} onSelect={setActiveId} />
            <InstagramPost post={activePost} slide={slide} onSlide={setSlide} />
            <div className="feed-footer"><RefreshCw size={15} /><span>검수 이미지는 비공개 R2 원본에서 불러옵니다.</span></div>
          </section>
          <ReviewInspector
            post={activePost}
            busy={busy}
            onSave={savePost}
            onApprove={approveActive}
            onPublishNow={publishNowActive}
            onReject={rejectActive}
            publisherReady={schedule?.credentialState === "ready"}
            onRegisterPasskey={registerPasskey}
            passkeyCount={session.passkeyCount}
          />
        </main>
      )}
      {view === "schedule" && schedule && <SchedulePage posts={posts} schedule={schedule} />}
      {view === "outro" && <OutroStudio posts={posts} onSelect={chooseOutro} busy={busy} />}
      {view === "reels" && (
        <ReelDownloads bundles={reelBundles} loading={reelsLoading} onCopyCaption={(caption) => void copyCaption(caption)} />
      )}
      {view === "collection" && (
        <CollectionReview
          collections={collections}
          loading={collectionsLoading}
          busy={busy}
          onPublish={(id) => void publishCollection(id)}
          onCopyCaption={(caption) => void copyCaption(caption)}
        />
      )}
      {view === "food" && (
        <FoodReview
          posts={foodPosts}
          loading={foodLoading}
          busy={busy}
          onPublish={(id) => void publishFood(id)}
          onCopyCaption={(caption) => void copyCaption(caption)}
          onRebuilt={(next) => setFoodPosts((current) => current.map((p) => p.id === next.id ? next : p))}
          onSlideError={(message) => showToast("error", message)}
          onDeletePost={(target) => void deleteFoodPost(target)}
        />
      )}
      <MobileNavigation view={view} onView={setView} />
      {toast && <div className={`toast ${toast.tone}`} role="status">{toast.tone === "success" ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}<span>{toast.message}</span></div>}
      {busy === "logout" && <div className="blocking-loader"><LoaderCircle className="spin" size={28} /></div>}
    </div>
  );
}
